<script setup>
import FormLabel from '~/components/Forms/Partials/FormLabel.vue'
import FormHelpText from '~/components/Forms/Partials/FormHelpText.vue'
import FormErrorMsg from '~/components/Forms/Partials/FormErrorMsg.vue'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  options: {
    type: Array,
    required: true
  },
  placeholder: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  errorMsg: {
    type: String,
    default: ''
  },
  helpText: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  showCheckIcon: {
    type: Boolean,
    default: false
  },
  inputClasses: {
    type: [String, Array, Object],
    default: ''
  },
  name: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue'])

const value = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const hasError = computed(() => !!props.errorMsg)
const hasValue = computed(() => props.modelValue !== '' && props.modelValue != null)
const isSuccess = computed(() => hasValue.value && !hasError.value && props.showCheckIcon)

const selectId = computed(() => props.name || props.label?.toLowerCase().replace(/\s+/g, '_') || `select_${Math.random().toString(36).substring(2, 9)}`)

const selectClasses = computed(() => [
  'w-full form-control-select pr-10',
  hasError.value ? 'form-control-error' : '',
  props.inputClasses
])
</script>

<template>
  <div class="block">
    <slot name="beforeLabel" />

    <FormLabel v-if="label" :for="selectId" :required="required">
      {{ label }}
    </FormLabel>

    <slot name="beforeInput" />

    <div class="relative">
      <slot name="inputPrefix" />

      <select
        :id="selectId"
        v-model="value"
        :name="name"
        :disabled="disabled"
        :readonly="readonly"
        :class="selectClasses"
      >
        <option v-if="placeholder && !value" value="">{{ placeholder }}</option>
        <option
          v-for="option in options"
          :key="option.optionValue"
          :value="option.optionValue"
        >
          {{ option.optionId }}
        </option>
      </select>

      <Icon
        name="mdi:chevron-down"
        class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 text-sm"
      />

      <Icon
        v-if="isSuccess"
        name="mdi:check-circle"
        class="absolute right-10 top-1/2 -translate-y-1/2 text-green-500 text-sm pointer-events-none"
      />
      <Icon
        v-if="hasError"
        name="mdi:alert-circle"
        class="absolute right-10 top-1/2 -translate-y-1/2 text-red-500 text-sm pointer-events-none"
      />

      <slot name="inputSuffix" />
    </div>

    <FormErrorMsg v-if="errorMsg" :text="errorMsg" />

    <FormHelpText v-else-if="helpText" :text="helpText" />

    <slot name="afterInput" />
  </div>
</template>
